import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {NGXLogger} from 'ngx-logger';
import {
	DiscrepanzaDetailsApiDescriptor,
	DiscrepanzaUpdateApiDescriptor,
	MagazzinoApiDescriptor,
	ModelloDispositivoApiDescriptor,
	MovimentazioneApiBuilder,
	MovimentazioneApiDescriptor, ReportChiusuraMovimentazioniPratica,
	ScortaMaterialeApiDescriptor, ScortaScuolaMaterialeApiDescriptor,
	ScuolaPraticaApiDescriptor
} from "../models/magazzino.model";
import {RegioneDTO} from "../models/regione.model";


@Injectable({
  providedIn: 'root',
})
export class MagazzinoService{

	private headers = {};
	apiUrl: string = environment.magazzino.apiUrl;

	constructor (
		private http: HttpClient,
		private logger: NGXLogger
	) {
		this.headers = {
			"Content-Type": "application/json",
		};
		this.headers[environment.magazzino.auth_token.header] = environment.magazzino.auth_token.value;
	}

	private getHeaders () {
		return {...this.headers};
	}

	private getFromBackend<T> (path: string, params: {[key: string]: any} = {}, headers: {[key: string]: any} = {}) {
		headers = {
			...this.getHeaders(),
			...headers
		};

		return this.http.get<T>(this.apiUrl + path, {
			params: params,
			headers: headers
		});
	}

	private putToBackend<T> (path: string, payload: {[key: string]: any}, headers: {[key: string]: any} = {}) {
		headers = {
			...this.getHeaders(),
			...headers
		};
		return this.http.put<T>(this.apiUrl + path, payload, {headers: headers });
	}

	private postToBackend<T> (path: string, payload: {[key: string]: any}, headers: {[key: string]: any} = {}) {
		headers = {
			...this.getHeaders(),
			...headers
		};
		return this.http.post<T>(this.apiUrl + path, payload, {headers: headers });
	}

	private deleteFromBackend<T> (path: string, headers: {[key: string]: any} = {}) {
		headers = {
			...this.getHeaders(),
			...headers
		};
		return this.http.delete<T>(this.apiUrl + path, {headers: headers });
	}

	// SUB METHODS

	regioneList () {
		return this.getFromBackend<{listing: RegioneDTO[]}>("regioni/");
	}

	modelloDispositivoList () {
		return this.getFromBackend<{listing: ModelloDispositivoApiDescriptor[]}>("materiali/");
	}

	// SUB METHODS - MAGAZZINI

	magazzinoList (aziendaid: number = null) {
		const params = {};
		if (aziendaid != null) {
			params["azienda"] = aziendaid;
		}
		return this.getFromBackend<{ listing: MagazzinoApiDescriptor[]}>("magazzino/", params);
	}

	magazzinoUpdate (itemid: string, edits: MagazzinoApiDescriptor) {
		return this.putToBackend<{ item: MagazzinoApiDescriptor }>(`magazzino/${itemid}/`, { values: edits });
	}

	magazzinoCreate (item: MagazzinoApiDescriptor) {
		return this.postToBackend<{ item: MagazzinoApiDescriptor}>("magazzino/", {values: item});
	}

	magazzinoDelete (itemid: string) {
		return this.deleteFromBackend<{ item: MagazzinoApiDescriptor }>(`magazzino/${itemid}/`);
	}

	// SUB METHODS - MOVIMENTAZIONI

	movimentazioneList (aziendaid: number = null, filter_transito: boolean = false) {
		const params = {};
		if (aziendaid != null) {
			params["azienda"] = aziendaid;
		}
		if (filter_transito) {
			params["transito"] = true
		}

		console.log("getting movs w azienda ", aziendaid);
		return this.getFromBackend<{ listing: MovimentazioneApiDescriptor[]}>("movimentazione/", params);
	}

	movimentazioneSetTransfer (itemid: string, direction_delivery: boolean, data: {}) {
		const actionpath = direction_delivery ? "set_received" : "set_sent";
		return this.postToBackend<{ item: MovimentazioneApiDescriptor}>(`movimentazione/${itemid}/${actionpath}/`, {values: data});
	}

	movimentazioneCreate (item: MovimentazioneApiBuilder, auto_create_transfer: boolean) {
		const payload = {
			values: item,
			params: {
				create_transfer: auto_create_transfer
			}
		}
		return this.postToBackend<{ item: MovimentazioneApiDescriptor}>(`movimentazione/`, payload);
	}

	movimentazioneRevert (itemid: string) {
		const payload = {};
		return this.postToBackend<{ solution: MovimentazioneApiDescriptor}>(`movimentazione/${itemid}/revert`, payload);
	}

	discrepanzaGet (itemid: string) {
		return this.getFromBackend<{ item: DiscrepanzaDetailsApiDescriptor}>("discrepanze/" + itemid + "/", {});
	}

	discrepanzaResolve (itemid: string, res_state: string, res_counter: MovimentazioneApiBuilder) {
		const payload = {
			values: {
				state: res_state,
				counter: res_counter
			}
		}
		return this.postToBackend<{ updated: DiscrepanzaUpdateApiDescriptor}>(`discrepanze/${itemid}/resolve/` , payload);
	}

	// READONLY actions

	scorteCorrentiList (aziendaid?: number) {
		const params = {};
		if (aziendaid != null) {
			params["azienda"] = aziendaid
		}
		return this.getFromBackend<{listing: ScortaMaterialeApiDescriptor[]}>("scorte/", params);
	}

	scorteTotaliList () {
		return this.getFromBackend<{listing: ScortaMaterialeApiDescriptor[]}>("rifornimenti/", { exclude_empty: true});
	}

	transitiList () {
		return this.getFromBackend<{listing: MovimentazioneApiDescriptor[]}>("transiti/");
	}

	scuoleListing (aziendaid?: number) {
		const params = {};
		if (aziendaid) {
			params["azienda"] = aziendaid
		}
		return this.getFromBackend<{listing: ScuolaPraticaApiDescriptor[]}>("scuole/", params);
	}

	scorteScuoleList (cui?: string, azienda?: number) {
		const params = {};
		if (cui != null) {
			params["cui"] = cui;
		}
		if (azienda != null) {
			params["azienda"] = azienda;
		}

		return this.getFromBackend<{listing: ScortaScuolaMaterialeApiDescriptor[]}>("scortescuole/", params);
	}

	installabiliPraticaListing (praticaid: number) {
		return this.getFromBackend<{listing: { [key: string]: number }}>(`installazioni/${praticaid}/materiali/`);
	}

	movimentazioniAttivePraticaListing (praticaid: number) {
		return this.getFromBackend<{listing: object[]}>(`installazioni/${praticaid}/movimentazioni/`);
	}

	praticaConsegnaAnnulla (praticaid: number) {
		// chiude tutte le movimentazioni per la pratica e manda la lista
		return this.postToBackend<{ listing: MovimentazioneApiDescriptor[]}>(`installazioni/${praticaid}/annullamento/`, {});
	}

	praticaConsegnaConferma (praticaid: number, materiali: {[key: string]: number}, return_to: string) {
		const payload = {
			materiali: materiali,
			return_to: return_to
		}
		return this.postToBackend<ReportChiusuraMovimentazioniPratica>(`installazioni/${praticaid}/chiusura/`, payload);
	}

	allineamentiUpload (data: object) {
		const payload = {
			values: data
		}
		return this.postToBackend<{done: {[key: string]: string[]}, skipped: string[]}>(`allineamenti/upload/`, payload);
	}


}